import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import { injectStateService, StateService } from '@/shared/services/StateService';
import intlShape from '@/shared/utils/intlShape';
import enumCache from '@/shared/services/EnumCacheService';

import moment from 'moment';
import { FaFileExcel } from 'react-icons/fa';
import SimplePanel from '@/shared/components/SimplePanel';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import MultiFilterColumnHeader from '@/shared/components/table/MultiFilterColumnHeader';
import {
  extractSubtaskStatusFilters,
  extractSubtaskUserFilters,
  extractParameterFilters,
  extractSubtaskCompletedDateFilters,
} from '@/shared/utils/filterUtils';
import api from '@/shared/api';
import debounce from 'lodash-es/debounce';
import { listItemMsg, formatListValue } from '@/shared/components/FormattedListValue';
import Checkbox from '@/shared/components/input/Checkbox';
import ToastrService from '@/shared/services/ToastrService';
import SignalRService from '@/shared/services/SignalRService';
import handleHttpError from '@/shared/services/HandleHttpError';
import Button from '@/shared/components/Button';
import BatchRegistrationModal from '@/shared/components/registration/BatchRegistrationModal';
import SendEmailModal from '@/shared/components/email/MultiRelationsMailModal';
import BatchGenerateDocumentModal from '@/shared/components/BatchGenerateDocumentModal';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import DocumentSignatureRequestModal from '@/shared/components/DocumentSignatureRequestModal';

import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import YesNoModal from '@/shared/components/YesNoModal';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { getRegistrationModalPropsForTask } from '@/shared/components/registration/registrationHelpers';
import authService, { functionalities, privileges } from '@/shared/services/AuthService';
import ShareItemModal from '@/shared/components/ShareItemModal';
import { getRelationName } from '@/shared/components/RelationName';
import { getShareItemNameForTask } from '@/shared/services/ObjectDisplayService';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import createDOMPurify from 'dompurify';
import { openTaskDocumentationTab } from '@/shared/utils/openTaskDocumentationTab';
import { showWhenPermission } from '@/shared/components/security';
import ResetDeadlineModal from '../ResetDeadlineModal';
import ChangeSubtaskFieldModal from '../ChangeSubtaskFieldModal';
import TaskDetailsModal from '../taskDetails/TaskDetailsModal';
import messages from '../taskMessages';
import TaskProgressBadge from '../components/TaskProgressBadge';
import ChangeTaskStatusModal from '../ChangeTaskStatusModal';
import TaskGlobalActionsButton from './TaskGlobalActionsButton';
import TaskOverviewFilters, { defaultFilters } from './TaskOverviewFilters';
import TaskOverviewRow from './TaskOverviewRow';
import config from '../../../config';
import ChangeTaskTagsModal from '../ChangeTaskTagsModal';
import UpdateRelationTagsModal from '@/modules/relations/overview/modals/UpdateRelationTagsModal';
import ChangeMainTaskFieldModal from './ChangeMainTaskFieldModal';

const sanitizer = createDOMPurify();
sanitizer.addHook('afterSanitizeAttributes', currentNode => {
  if ('target' in currentNode) {
    // Open all links in new tab / window
    currentNode.setAttribute('target', '_blank');

    // To prevent legacy browsers to pass confidential info across the link...
    // https://developer.chrome.com/docs/lighthouse/best-practices/external-anchors-use-rel-noopener
    currentNode.setAttribute('rel', 'noopener noreferrer');
  }
});

const mapParameterValues = item => {
  const result = { ...item };
  if (item.valueDate && typeof item.valueDate === 'string')
    result.valueDate = moment.utc(item.valueDate);
  if (item.relationFieldType === 15)
    result.valueObject = item.valueText ? JSON.parse(item.valueText) : null;
  return result;
};

const mapParent = (stateTask, updatedTask) =>
  updatedTask
    ? {
        ...stateTask,
        status: updatedTask.status,
        deadline: updatedTask.deadline,
        executionDate: updatedTask.executionDate,
        completedDate: updatedTask.completedDate,
        isBlocked: updatedTask.isBlocked,
        inapplicable: updatedTask.inapplicable,
        rejected: updatedTask.rejected,
        hasRemark: updatedTask.hasRemark,
        remark: updatedTask.remark,
        year: updatedTask.year,
        userId: updatedTask.userId,
        scheduleRemark:
          updatedTask.scheduleRemark === undefined
            ? stateTask.scheduleRemark
            : updatedTask.scheduleRemark, // SignalR event doesn't send schedule remark, keep from state if undefined
        parameterValues: !updatedTask.parameterValues
          ? stateTask.parameterValues
          : (updatedTask.parameterValues || []).map(mapParameterValues),
        changedCounter: (stateTask?.changedCounter || 0) + 1,
      }
    : stateTask;

const mapChild = (stateTask, subtask) => ({
  ...stateTask,
  remark: subtask.remark,
  status: subtask.status,
  deadline: subtask.deadline,
  executionDate: subtask.executionDate,
  completedDate: subtask.completedDate,
  isBlocked: subtask.isBlocked,
  inapplicable: subtask.inapplicable,
  rejected: subtask.rejected,
  hasRemark: subtask.hasRemark,
  hasAttachments: subtask.hasAttachments,
  userId: subtask.userId,
  changedCounter: (stateTask?.changedCounter || 0) + 1,
});

const getParameterDefinitionFilter = (definition, intl, pitTaxReturnTypes) => {
  if (definition.parameterValueType === 4) {
    const selectionItemsArray = definition.selectionItems
      ? definition.selectionItems.split(';')
      : [];

    return {
      type: 'items',
      key: `parameter_${definition.id}`,
      items: ['select_$NOTFILLEDOUT$', ...selectionItemsArray.map(x => `select_${x}`)],
      renderItem: item =>
        item.split('_')[1] === '$NOTFILLEDOUT$'
          ? intl.formatMessage(messages.NotFilledOut)
          : item.split('_')[1],
    };
  }
  if (definition.systemParameterType === 26) {
    return {
      type: 'items',
      key: `parameter_${definition.id}`,
      items: ['select_$NOTFILLEDOUT$', ...pitTaxReturnTypes.map(x => `select_${x.key}`)],
      renderItem: item =>
        item.split('_')[1] === '$NOTFILLEDOUT$'
          ? intl.formatMessage(messages.NotFilledOut)
          : intl.formatMessage(listItemMsg('pitTaxReturnType', item.split('_')[1])),
    };
  }
  if (definition.parameterValueType === 5) {
    return {
      type: 'user',
      key: `parameter_${definition.id}`,
    };
  }
  return {
    type: 'items',
    key: `parameter_${definition.id}`,
    items: [`bool_${true}`, `bool_${false}`],
    renderItem: item =>
      new RegExp('true').test(item.split('_')[1]) === true
        ? intl.formatMessage(messages.FilledOut)
        : intl.formatMessage(messages.NotFilledOut),
  };
};
const mapParameterDefinition = (definition, intl, pitTaxReturnTypes) => ({
  ...definition,
  filter: getParameterDefinitionFilter(definition, intl, pitTaxReturnTypes),
});

const buildFilterParams = (searchParams, { includePaging = false } = {}) => {
  const filter = searchParams?.filter || {};

  const filterParams = {
    ...searchParams?.taskFilters,
    statusFilters: extractSubtaskStatusFilters(filter),
    userFilters: extractSubtaskUserFilters(filter),
    parameterFilters: extractParameterFilters(filter),
    completedDateFilters: extractSubtaskCompletedDateFilters(filter),
    mainStatus: filter.mainStatus?.items || null,
    withRequiredAuthorizationWarning: filter.withRequiredAuthorizationWarning?.items,
    mainTaskUserFilter: filter.mainTaskUserFilter?.items?.[0] || null,
  };

  if (!includePaging) return filterParams;

  return {
    ...filterParams,
    page: searchParams.page,
    take: searchParams.pageSize,
    sort: searchParams.sortKey,
    desc: searchParams.desc,
  };
};
class TaskOverviewContainer extends Component {
  state = {
    displayMode: 'status',
    allTasksSelected: false,
    items: [],
    columns: [],
    languages: [],
    initialSearchParams: {
      page: 0,
      pageSize: 50,
      sortKey: 'relation',
      desc: false, // relations are better sorted alphabetically
      taskFilters: {
        ...defaultFilters,
      },
    },
    currentSearchParams: undefined,

    selectedRows: {},
    highlightedRows: {},
  };

  jobUpdateReloadTasks = debounce(() => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.currentSearchParams,
        // forces refresh of table
        random: new Date().getTime(),
      },
    }));
  }, 500);

  taskUpdatedEventHandler = task => {
    if (!task) return;
    this.handleTasksUpdated([task]);
  };

  retrieveTemplates() {
    const { intl } = this.props;
    Promise.all([
      api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`),
      enumCache.get('PitTaxReturnType'),
    ]).then(([{ data }, pitTaxReturnTypes]) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
        }))
        .sort((a, b) => (a.label > b.label) - (a.label < b.label)); // Sort on label property

      this.setState({
        templates: translatedTemplates,
        pitTaxReturnTypes,
      });
    });
  }

  taskRemovedEventHandler = taskIds => {
    const { items } = this.state;
    const itemsCopy = items.filter(task => !taskIds.includes(task.id));

    this.setState(state => ({
      ...state,
      items: [...itemsCopy],
    }));
  };

  taskHasAttachmentsChangedHandler = message => {
    this.setState(({ items }) => {
      const updatedItems = [...items];
      if (message.level === 0) {
        const index = updatedItems.findIndex(x => x.id === message.id);
        if (index < 0) return;
        const updatedRow = {
          ...updatedItems[index],
          hasAttachments: message.hasAttachments,
        };

        updatedItems[index] = updatedRow;
      } else if (message.level === 1) {
        const mainTaskIndex = updatedItems.findIndex(x => x.id === message.parentId);
        if (mainTaskIndex < 0) return;

        const updatedRow = {
          ...updatedItems[mainTaskIndex],
          hasAttachments: message.parentHasAttachments,
        };
        const taskTemplateIds = Object.keys(updatedRow.columns);

        // eslint-disable-next-line
        for (const taskTemplateId of taskTemplateIds) {
          const column = updatedRow.columns[taskTemplateId];
          if (column.id === message.id) {
            updatedRow.columns = {
              ...updatedRow.columns,
              [taskTemplateId]: {
                ...updatedRow.columns[taskTemplateId],
                hasAttachments: message.hasAttachments,
              },
            };
            updatedItems[mainTaskIndex] = updatedRow;
            break;
          }
        }
      }

      return { items: updatedItems };
    });
  };

  componentDidMount() {
    SignalRService.subscribe('taskUpdated', this.taskUpdatedEventHandler);
    SignalRService.subscribe('taskDeleted', this.taskRemovedEventHandler);
    SignalRService.subscribe('taskHasAttachmentsChanged', this.taskHasAttachmentsChangedHandler);

    this.retrieveTemplates();
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('taskUpdated', this.taskUpdatedEventHandler);
    SignalRService.unsubscribe('taskDeleted', this.taskRemovedEventHandler);
    SignalRService.unsubscribe('taskHasAttachmentsChanged', this.taskHasAttachmentsChangedHandler);

    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
    if (this.summaryCancelToken && !this.summaryCancelToken.token.reason) {
      this.summaryCancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  constructor(props) {
    super(props);
    const { initialSearchParams: orgSearchParams } = this.state;

    let filtersFromSearchParams = {};
    if (props.searchParams && Object.keys(props.searchParams).length) {
      const {
        mainStatus,
        columnUserFilters = null,
        columnStatusFilters = null,
        deadlineFrom,
        deadlineUntil,
        executionDateFrom,
        executionDateUntil,
        period,
        relation = null,
        mainTaskUserFilter,
        ...rest
      } = props.searchParams;

      const filter = {}; // Clear column filters
      const taskFilters = {
        ...orgSearchParams.taskFilters,
        ...rest,
      };
      if (mainStatus) filter.mainStatus = { items: mainStatus };
      if (mainTaskUserFilter) filter.mainTaskUserFilter = { items: [mainTaskUserFilter] };
      if (columnUserFilters && typeof columnUserFilters === 'object') {
        Object.entries(columnUserFilters).forEach(([subtaskTemplateId, subtaskUserId]) => {
          if (subtaskUserId) {
            filter[`subtask_user_${subtaskTemplateId}`] = { items: [subtaskUserId] };
          }
        });
      }
      if (columnStatusFilters && typeof columnStatusFilters === 'object') {
        Object.entries(columnStatusFilters).forEach(([subtaskTemplateId, statusItems]) => {
          if (Array.isArray(statusItems) && statusItems.length > 0) {
            filter[`subtask_${subtaskTemplateId}`] = { items: statusItems };
          }
        });
      }
      if (period) {
        taskFilters.period = period;
      }
      if (deadlineFrom) {
        taskFilters.deadlineFrom = moment.utc(deadlineFrom);
        taskFilters.period = null;
      }
      if (deadlineUntil) {
        taskFilters.deadlineUntil = moment.utc(deadlineUntil);
        taskFilters.period = null;
      }

      taskFilters.executionDateFrom = executionDateFrom ? moment.utc(executionDateFrom) : undefined;
      taskFilters.executionDateUntil = executionDateUntil
        ? moment.utc(executionDateUntil)
        : undefined;
      if (relation && relation.id && relation.name) {
        taskFilters.relationId = relation.id;
        taskFilters.relationName = relation.name;
        taskFilters.invoiceStreamId = relation.invoiceStreamId;
      }
      filtersFromSearchParams = { filter, taskFilters };
      // We removed the return that was here in order to have session sorting to prevail, even if
      // filters are taken from passed in searchParams
    }

    const sessionParams = JSON.parse(sessionStorage.getItem('TaskOverviewParams')) || {};
    if (sessionParams.taskFilters) {
      if (sessionParams.taskFilters.deadlineFrom)
        sessionParams.taskFilters.deadlineFrom = moment.utc(sessionParams.taskFilters.deadlineFrom);
      if (sessionParams.taskFilters.deadlineUntil)
        sessionParams.taskFilters.deadlineUntil = moment.utc(
          sessionParams.taskFilters.deadlineUntil,
        );

      if (sessionParams.taskFilters.executionDateFrom)
        sessionParams.taskFilters.executionDateFrom = moment.utc(
          sessionParams.taskFilters.executionDateFrom,
        );
      if (sessionParams.taskFilters.executionDateUntil)
        sessionParams.taskFilters.executionDateUntil = moment.utc(
          sessionParams.taskFilters.executionDateUntil,
        );

      // Migration from previous stored session params where this might not be defined resulting in errors
      if (!sessionParams.taskFilters.relationTypes) {
        sessionParams.taskFilters.relationTypes = [];
      }
      if (!sessionParams.taskFilters.ownCompanies) {
        sessionParams.taskFilters.ownCompanies = [];
      }
      if (!sessionParams.taskFilters.legalEntities) {
        sessionParams.taskFilters.legalEntities = [];
      }
    }

    // This is our fallback procedure, use filters/taskFilters from searchParams, then sessionStore, then orgParams
    // in order to keep sorting parameters from sessionStore
    this.state.initialSearchParams = {
      ...orgSearchParams,
      ...sessionParams,
      ...filtersFromSearchParams,
    };
    if (sessionParams.displayMode) this.state.displayMode = sessionParams.displayMode;

    if (this.state.initialSearchParams.sortKey) {
      const subtaskMatch = this.state.initialSearchParams.sortKey.match(
        /subtask_([^_]+)_[0-9a-fA-F-]+/i,
      );
      if (subtaskMatch && subtaskMatch.length > 1) {
        // eslint-disable-next-line prefer-destructuring
        this.state.displayMode = subtaskMatch[1];
      }
    }
  }

  static propTypes = {
    intl: intlShape.isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
  };

  loadSummary = (searchParams, reset = true) => {
    searchParams = searchParams || this.state.currentSearchParams;
    const endpoint = `${config.appUrls.tasks}/api/Overview/Summary`;

    if (reset) this.setState({ columnStatistics: {} });

    // Its own cancel token: the summary covers the whole filtered set, so loading another page must
    // not abort it. Only a new summary request replaces the previous one.
    if (typeof this.summaryCancelToken !== 'undefined') {
      this.summaryCancelToken.cancel('Operation canceled due to new request.');
    }
    this.summaryCancelToken = Axios.CancelToken.source();

    api.base
      .post(endpoint, buildFilterParams(searchParams), {
        cancelToken: this.summaryCancelToken.token,
      })
      .then(({ data }) => {
        const columnStatistics = data.reduce(
          (obj, column) => ({ ...obj, [column.taskTemplateId]: column }),
          {},
        );
        this.setState({ columnStatistics });
      })
      .catch(error => {
        if (Axios.isCancel(error)) return;
        handleHttpError(this.props.intl)(error);
      });
  };

  loadItems = searchParams => {
    const { displayMode, allTasksSelected } = this.state;
    const { intl } = this.props;
    if (!searchParams.taskFilters.taskId) {
      return Promise.resolve([]); // Task id is required
    }

    if (typeof this.cancelToken !== 'undefined') {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();
    const filter = searchParams.filter || {};

    sessionStorage.setItem(
      'TaskOverviewParams',
      JSON.stringify({
        taskFilters: searchParams.taskFilters,
        parameterFilters: searchParams.parameterFilters,
        sortKey: searchParams.sortKey,
        desc: searchParams.desc,
        filter,
        displayMode,
      }),
    );

    const requestBody = buildFilterParams(searchParams, { includePaging: true });
    const requestConfig = { cancelToken: this.cancelToken.token };

    // Both the count and the summary describe the whole filtered set instead of the current page, so
    // they only need refreshing when paging restarts, not on every lazy loaded page.
    if (searchParams.page === 0) {
      this.loadCount(requestBody, requestConfig);

      if (authService.hasFeature('CapacityPlanning')) {
        this.loadSummary(searchParams);
      }
    }

    return api.base
      .post(`${config.appUrls.tasks}/api/Overview`, requestBody, requestConfig)
      .then(({ data }) => {
        if (!data) {
          // Something wrong with the parameters or the tasktemplate is removed
          this.setState({
            items: [],
            columns: [],
            taskTemplateName: null,
            taskTemplateId: null,
            taskTemplateHasDocumentation: false,
            totalRows: 0,
            currentSearchParams: searchParams,
            defaultResourceId: null,
            parameterDefinitions: null,
            requiredAuthorizations: [],
            selectedRows: {},
            highlightedRows: {},
          });
          return [];
        }
        // If checkbox for all tasks is selected, set selected on all new incoming tasks
        const newTasks = data.rows.map(e => ({
          ...e,
          changedCounter: 0,
          parameterValues: (e.parameterValues || []).map(mapParameterValues),
        }));
        const newSelectedTasks = allTasksSelected
          ? newTasks.reduce((o, x) => {
              o[x.id] = true;
              return o;
            }, {})
          : {};

        this.setState(state => ({
          items: searchParams.page > 0 ? [...state.items, ...newTasks] : [...newTasks],
          columns: searchParams.page > 0 ? state.columns : data.columns,
          taskTemplateName: data.taskTemplateName,
          taskTemplateId: data.taskTemplateId,
          taskTemplateHasDocumentation: data.hasDocumentation,
          currentSearchParams: searchParams,
          defaultResourceId: data.resourceId,
          parameterDefinitions: data.parameterDefinitions?.map(x =>
            mapParameterDefinition(x, intl, state.pitTaxReturnTypes),
          ),
          requiredAuthorizations: data.requiredAuthorizations,
          selectedRows:
            searchParams.page > 0
              ? { ...state.selectedRows, ...newSelectedTasks }
              : newSelectedTasks,
          highlightedRows: searchParams.page > 0 ? state.highlightedRows : {},
        }));
        return data.rows;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        return [];
      });
  };

  loadCount = (requestBody, requestConfig) => {
    api.base
      .post(`${config.appUrls.tasks}/api/Overview/Count`, requestBody, requestConfig)
      .then(({ data }) => {
        this.setState({ totalRows: data });
      })
      .catch(error => {
        if (Axios.isCancel(error)) return;
        handleHttpError(this.props.intl)(error);
      });
  };
  handleTaskSelection = task => {
    this.setState(({ selectedRows }) => ({
      selectedRows: { ...selectedRows, [task.id]: !selectedRows[task.id] },
    }));
  };
  handleRowHighlight = task => {
    this.setState(({ highlightedRows }) => ({
      highlightedRows: { ...highlightedRows, [task.id]: !highlightedRows[task.id] },
    }));
  };

  filtersChanged = filters => {
    const { initialSearchParams, currentSearchParams } = this.state;

    const {
      taskFilters: { taskId: orgTaskId },
    } = initialSearchParams;
    const { columnFilters, ...taskFilters } = filters;

    const params = currentSearchParams || initialSearchParams || {};
    const taskChanged = orgTaskId !== filters.taskId;

    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        taskFilters,
        // if columnFilters has a value, this is from a saved filter
        filter: columnFilters || (taskChanged ? {} : params.filter) || {},
        sortKey: taskChanged ? 'deadline' : params.sortKey,
        desc: taskChanged ? false : params.desc,
      },
    }));
  };

  displayModeChanged = displayMode => {
    this.setState(
      ({ initialSearchParams, currentSearchParams }) => {
        const params = currentSearchParams || initialSearchParams || {};
        if (!params.sortKey || params.sortKey.indexOf('subtask_') < 0)
          return {
            displayMode: displayMode || 'status',
          };

        const newSortKey = params.sortKey.replace(
          /subtask(?:_[^_]+)?_([0-9a-fA-F-]+)/gi,
          `subtask_${displayMode || 'status'}_$1`,
        );
        if (newSortKey === params.sortKey)
          return {
            displayMode: displayMode || 'status',
          };

        return {
          displayMode: displayMode || 'status',
          initialSearchParams: {
            ...initialSearchParams,
            ...params,
            page: 0,
            sortKey: newSortKey,
          },
        };
      },
      () => {
        const sessionParams = JSON.parse(sessionStorage.getItem('TaskOverviewParams')) || {};
        sessionParams.displayMode = displayMode || 'status';
        sessionStorage.setItem('TaskOverviewParams', JSON.stringify(sessionParams));
      },
    );
  };

  handleSelectAllTasks = () => {
    this.setState(({ items, allTasksSelected }) => ({
      allTasksSelected: !allTasksSelected,
      selectedRows: !allTasksSelected
        ? items.reduce((o, x) => {
            o[x.id] = true;
            return o;
          }, {})
        : {},
    }));
  };

  getCountSelectedTasksFormatted() {
    const { totalRows } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelectedTasks();

    return intl.formatMessage(messages.CountTasksSelected, { count, total: totalRows });
  }

  getCountSelectedTasks() {
    const { allTasksSelected, totalRows, items, selectedRows } = this.state;

    const count = items ? items.filter(x => !!selectedRows[x.id] !== allTasksSelected).length : 0;

    return allTasksSelected ? totalRows - count : count;
  }

  isGlobalActionsDisabled = () => {
    const { items, allTasksSelected, selectedRows } = this.state;
    if (!items || !items.length) return true;
    if (items.some(x => !!selectedRows[x.id]) || allTasksSelected) return false;

    return true;
  };

  createBatchActionModel = parameters => {
    const { currentSearchParams, items, allTasksSelected, selectedRows } = this.state;

    const filterParams = buildFilterParams(currentSearchParams, { includePaging: true });

    const model = {
      filterParams,
      parameters,
      selectAll: allTasksSelected,
      selectedTasks: items
        ? items.filter(x => !!selectedRows[x.id] !== allTasksSelected).map(x => x.id)
        : [],
    };

    return model;
  };

  executeBatchActionTaskStatus = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;
    const { currentSearchParams } = this.state;

    const model = this.createBatchActionModel({
      ...batchActionParameters,
      mainTaskTemplateId: currentSearchParams.taskFilters.taskId,
    });

    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/BatchChangeTaskStatus`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showChangeTaskStatusModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.UpdateTaskStatusQueued), undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionDeleteTask = () => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl } = this.props;
    const { currentSearchParams } = this.state;

    const model = this.createBatchActionModel({
      mainTaskTemplateId: currentSearchParams.taskFilters.taskId,
    });

    const endpoint = `${config.appUrls.tasks}/api/Overview/BatchDeleteTasks`;

    return api.base
      .post(endpoint, model)
      .then(() => {
        this.setState({ showBatchDeleteTaskModal: false });
      })
      .catch(error => {
        this.setState({ showBatchDeleteTaskModal: false });
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionChangeField = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/BatchChangeField`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showChangeMainTaskFieldModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        const message =
          batchActionParameters.field === 0
            ? intl.formatMessage(messages.UpdateTaskDeadlineQueued)
            : batchActionParameters.field === 3
            ? intl.formatMessage(messages.UpdateTaskExecutionDateQueued)
            : batchActionParameters.field === 4
            ? intl.formatMessage(messages.UpdateTaskParameterQueued)
            : intl.formatMessage(messages.UpdateTaskStatusQueued);
        ToastrService.info(message, undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionSubtaskDeadline = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));
    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/BatchChangeSubtaskDeadline`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showChangeSubtaskDeadlineModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        const message = intl.formatMessage(messages.UpdateTaskDeadlineQueued);
        ToastrService.info(message, undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionSubtaskExecutionDate = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));
    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/BatchChangeSubtaskExecutionDate`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showChangeSubtaskExecutionDateModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(
          intl.formatMessage(messages.UpdateTaskExecutionDateQueued),
          undefined,
          onClick,
        );
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionTaskRegistration = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/BatchTaskRegistration`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showNewRegistrationModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.NewRegistrationsQueued), undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionSendTaskRelationMail = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/BatchMailTaskRelation`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showSendMailModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.SendEmailQueued), undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  executeBatchActionGenerateDocuments = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/Overview/BatchGenerateDocuments`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showGenerateDocumentsModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(
          intl.formatMessage(messages.GenerateDocumentsQueued),
          undefined,
          onClick,
        );
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  executeBatchActionResetDeadline = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = this.createBatchActionModel(batchActionParameters);
    const endpoint = `${config.appUrls.tasks}/api/Overview/BatchResetDeadlines`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showResetDeadlineModal: false,
        });
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(
          intl.formatMessage(messages.UpdateTaskDeadlineQueued),
          undefined,
          onClick,
        );
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  executeBatchActionChangeTags = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = { ...batchActionParameters, ...this.createBatchActionModel() };
    const endpoint = `${config.appUrls.tasks}/api/Overview/BatchChangeTags`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showChangeTagsModal: false,
        });
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionUpdateRelationTags = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = { ...batchActionParameters, ...this.createBatchActionModel() };
    const endpoint = `${config.appUrls.tasks}/api/Overview/BatchUpdateRelationTags`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showUpdateRelationTagsModal: false,
        });
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        const successMessage =
          batchActionParameters.action === 'add'
            ? intl.formatMessage(messages.RelationLabelsAddedQueued)
            : intl.formatMessage(messages.RelationLabelsRemovedQueued);
        ToastrService.info(successMessage, undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  createReport = () => {
    const { intl } = this.props;
    const { currentSearchParams, items, allTasksSelected, displayMode, selectedRows } = this.state;

    const model = {
      ...buildFilterParams(currentSearchParams, { includePaging: true }),
      selectAll: allTasksSelected,
      selectedTasks: items
        ? items.filter(x => !!selectedRows[x.id] !== allTasksSelected).map(x => x.id)
        : [],
    };

    const culture = intl.locale;
    const displayModeMap = { status: 1, dateDone: 2, deadline: 3, executionDate: 4, assignedTo: 5 };
    const mappedDisplayMode = displayModeMap[displayMode] || 1;
    const endpoint = `${
      config.appUrls.tasks
    }/api/overview/export?displayMode=${mappedDisplayMode}&culture=${culture}&connectionId=${SignalRService.getConnectionId()}`;
    this.setState(state => ({
      ...state,
      creatingReport: true,
    }));

    return api.base
      .post(endpoint, model)
      .then(result => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState(state => ({
          ...state,
          creatingReport: false,
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          creatingReport: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  onShowTaskDetails = taskId => {
    this.setState(state => ({
      ...state,
      activeTask: { id: taskId },
      showTaskDetailsModal: true,
    }));
  };

  onTaskDetailsClosed = () => {
    this.setState({
      showTaskDetailsModal: false,
    });
  };

  onTaskDetailModalClosed = updatedTasks => {
    if (updatedTasks && updatedTasks.length) {
      this.setState(
        state => ({
          ...state,
          showTaskDetailsModal: false,
        }),
        () => updatedTasks.map(task => this.taskUpdatedEventHandler(task)),
      );
    } else {
      this.setState({ showTaskDetailsModal: false });
    }
  };

  onAddRegistrationClick = task => {
    this.setState(state => ({
      ...state,
      activeTask: task,
      showRegistrationModal: true,
    }));
  };

  onRegistrationCreated = () => {
    this.setState({
      showRegistrationModal: false,
      activeTask: null,
    });
  };

  updateTask = task => {
    const { intl } = this.props;
    return api.base.put(`${config.appUrls.tasks}/api/plannedTasks`, task).then(
      ({ data }) => {
        ToastrService.changesSaved(intl);
        this.handleTasksUpdated([data]);
        return data;
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };
  updateDeadline = task => {
    const { intl } = this.props;

    const deadline = task.deadline ? task.deadline.format('YYYY-MM-DD') : '';
    return api.base
      .put(`${config.appUrls.tasks}/api/plannedTasks/deadline/${task.id}/${deadline}`, task)
      .then(
        ({ data }) => {
          ToastrService.changesSaved(intl);
          this.handleTasksUpdated([data]);
          return data;
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };
  updateExecutionDate = task => {
    const { intl } = this.props;

    const executionDate = task.executionDate ? task.executionDate.format('YYYY-MM-DD') : '';
    return api.base
      .put(
        `${config.appUrls.tasks}/api/plannedTasks/ExecutionDate/${task.id}/${executionDate}`,
        task,
      )
      .then(
        ({ data }) => {
          ToastrService.changesSaved(intl);
          this.handleTasksUpdated([data]);
          return data;
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };
  handleTasksUpdated = updatedTasks => {
    if (!updatedTasks || !updatedTasks.length) return;
    this.setState(
      ({ items }) => {
        let updatedItems = [...items];
        updatedTasks.forEach(task => {
          if (task.taskLevel === 0) {
            updatedItems = updatedItems.map(x => {
              if (x.id === task.id) {
                const updatedRow = mapParent(x, task);
                if (task.children && task.children.length) {
                  updatedRow.columns = {
                    ...task.children.reduce((columns, subtask) => {
                      // eslint-disable-next-line no-param-reassign
                      columns[subtask.templateId] = mapChild(columns[subtask.templateId], subtask);
                      return columns;
                    }, updatedRow.columns || {}),
                  };
                }
                return updatedRow;
              }

              // update schedule remark for other task (same relation) if present in this view (yes they could have been made by a different schedule ID but the chance is very low)
              if (x.id !== task.id && x.relation?.id === task.relationId && x.scheduleId)
                return {
                  ...x,
                  scheduleHasRemark: task.scheduleHasRemark,
                  scheduleRemark: task.scheduleRemark,
                };

              return x;
            });
          } else if (task.taskLevel === 1) {
            const index = updatedItems.findIndex(x => x.id === task.parentId);
            if (index < 0) return; // Task not on screen, skipping
            const updatedRow = mapParent(updatedItems[index], task.parent);
            updatedRow.columns = {
              ...updatedRow.columns,
              [task.templateId]: mapChild(updatedRow.columns[task.templateId], task),
            };
            updatedItems[index] = updatedRow;
          }
        });

        return { items: updatedItems };
      },
      () => {
        if (authService.hasFeature('CapacityPlanning')) {
          this.loadSummary(undefined, false);
        }
      },
    );
  };

  templateChanged = event => {
    const taskId = event.target.value;
    const {
      initialSearchParams: { taskFilters = {} },
    } = this.state;
    this.filtersChanged({ ...taskFilters, taskId, periodicityId: '' });
  };
  onShareItem = task => {
    this.setState({ showShareItemModal: true, activeTask: task });
  };

  createAuthorizationReport = () => {
    const { intl } = this.props;
    const { currentSearchParams, items, allTasksSelected, selectedRows } = this.state;

    const model = {
      ...buildFilterParams(currentSearchParams, { includePaging: true }),
      selectAll: allTasksSelected,
      selectedTasks: items
        ? items.filter(x => !!selectedRows[x.id] !== allTasksSelected).map(x => x.id)
        : [],
    };

    const culture = intl.locale;

    const endpoint = `${
      config.appUrls.tasks
    }/api/overview/export/authorizations?culture=${culture}&connectionId=${SignalRService.getConnectionId()}`;
    this.setState({
      creatingAuthorizationReport: true,
    });

    return api.base
      .post(endpoint, model)
      .then(result => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState({ creatingAuthorizationReport: false });
      })
      .catch(error => {
        this.setState({ creatingAuthorizationReport: false });
        handleHttpError(intl)(error);
      });
  };

  renderRow = item => {
    const { intl } = this.props;
    const {
      columns,
      initialSearchParams,
      displayMode,
      parameterDefinitions,
      requiredAuthorizations,
      selectedRows,
      highlightedRows,
    } = this.state;

    return (
      <TaskOverviewRow
        key={item.id}
        remark={item.remark}
        task={item}
        columns={columns}
        taskFilters={initialSearchParams.taskFilters}
        requiredAuthorizations={requiredAuthorizations}
        handleTaskSelection={this.handleTaskSelection}
        handleRowHighlight={this.handleRowHighlight}
        displayMode={displayMode}
        isHighlighted={highlightedRows[item.id]}
        isSelected={selectedRows[item.id]}
        intl={intl}
        parameterDefinitions={parameterDefinitions}
        onShowTaskDetails={this.onShowTaskDetails}
        onTaskDetailsClosed={this.onTaskDetailsClosed}
        onAddRegistrationClick={this.onAddRegistrationClick}
        onTasksUpdated={this.handleTasksUpdated}
        onRequestDeleteTask={this.onRequestDeleteTask}
        onUpdateParameter={this.onUpdateParameter}
        onShareItem={this.onShareItem}
        onUpdateDeadline={this.updateDeadline}
        onUpdateExecutionDate={this.updateExecutionDate}
        onSendSignatureRequest={this.onSendSignatureRequest}
      />
    );
  };

  onTaskDeleted = task => {
    this.setState(state => ({
      ...state,
      items: state.items.filter(row => row.id !== task.id),
    }));
  };

  onRequestDeleteTask = task => {
    this.setState(state => ({
      ...state,
      activeTask: task,
      showDeleteTaskModal: true,
    }));
  };

  deleteTask = () => {
    const { activeTask, items } = this.state;
    const { intl } = this.props;

    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/${activeTask.id}`;
    return api.base
      .delete(endpoint, { params: { taskLevel: activeTask.taskLevel } })
      .then(() => {
        ToastrService.success(intl.formatMessage(messages.TaskDeleted));

        let itemsCopy = items;
        itemsCopy = items.filter(task => task.id !== activeTask.id);

        this.setState(state => ({
          ...state,
          showDeleteTaskModal: false,
          items: [...itemsCopy],
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          showDeleteTaskModal: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  onUpdateParameter = parameter => {
    const { intl } = this.props;

    if (!!parameter.computedParameterType) {
      parameter.computedParameterModifiedByUser = true;
    }
    const endpoint = `${config.appUrls.tasks}/api/plannedTasks/UpdateParameters`;
    const task = this.state.items.find(x => x.id === parameter.plannedTaskId);
    const parameterValues = task.parameterValues.map(pv =>
      pv.id === parameter.id ? parameter : pv,
    );

    return api.base
      .post(endpoint, Object.assign(task, { parameterValues }))
      .then(({ data }) => {
        this.setState(state => ({
          items: state.items.map(task => {
            if (task.id !== parameter.plannedTaskId) return task;
            const parameterValues = task.parameterValues
              .map(pv =>
                pv.id === parameter.id
                  ? parameter
                  : pv.computedParameterModifiedByUser
                  ? pv
                  : data.filter(x => pv.id === x.id)[0] || pv,
              )
              .map(pv => mapParameterValues(pv));
            return { ...task, parameterValues };
          }),
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
        }));
        handleHttpError(intl)(error);
      });
  };

  openSendEmailModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showSendMailModal: true,
          languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  openGenerateDocumentsModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showGenerateDocumentsModal: true,
          languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  openChangeSubtaskStatusModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showChangeTaskStatusModal: true,
          languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  openChangeMainTaskFieldModal = () => {
    const { intl } = this.props;
    const { taskTemplateId, pitTaxReturnTypes } = this.state;

    this.setState({
      showChangeMainTaskFieldModal: true,
      allParameterDefinitions: null,
    });

    if (!taskTemplateId) return;

    api.base
      .get(`${config.appUrls.tasks}/api/TaskParameters/Definitions/${taskTemplateId}`)
      .then(({ data }) => {
        this.setState({
          allParameterDefinitions: (data || []).map(x =>
            mapParameterDefinition(x, intl, pitTaxReturnTypes),
          ),
        });
      })
      .catch(handleHttpError(intl));
  };

  getSelectedRelationLanguages = () => {
    const { allTasksSelected, items, selectedRows } = this.state;
    return new Promise((resolve, reject) => {
      if (allTasksSelected) {
        const { currentSearchParams } = this.state;

        api.base
          .post(
            `${config.appUrls.tasks}/api/overview/OverviewLanguages`,
            buildFilterParams(currentSearchParams),
          )
          .then(({ data }) => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        const languages = [
          ...new Set(items.filter(x => !!selectedRows[x.id]).map(x => x.relation.languageCode)),
        ]; // This makes sure it is a distinct list of language codes
        resolve(languages);
      }
    });
  };

  onSendSignatureRequest = task =>
    api.base.get(`${config.appUrls.tasks}/api/plannedtasks/ChildTaskIds/${task.id}`).then(
      ({ data }) => {
        this.setState({ taskIds: [task.id, ...data] }, () => {
          const { taskIds } = this.state;
          return this.retrieveTaskAttachments(taskIds);
        });
      },
      () => this.retrieveTaskAttachments([task.id]),
    );

  retrieveTaskAttachments = taskIds => {
    const { intl } = this.props;
    const linkedDocumentsPromise = api.base.post(
      `${config.appUrls.documents}/api/documents/DocumentLinks`,
      taskIds,
    );

    return Promise.all([linkedDocumentsPromise]).then(
      ([{ data: attachments }]) => {
        const signableDocuments = attachments.filter(x => x.canBeSigned);
        if (!signableDocuments || signableDocuments.length === 0) {
          ToastrService.error(intl.formatMessage(messages.NoSignableDocumentsLinkedToTask));
        } else {
          this.setState({
            showSelectDocumentsModal: true,
            signableDocuments,
          });
        }
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  openSendSignatureRequestModal = documents => {
    this.setState({ showSelectDocumentsModal: false });
    const selectedDocuments = documents.filter(x => x.selected);
    if (!selectedDocuments || selectedDocuments.length === 0) return;

    this.setState({ activeDocuments: selectedDocuments, showSignatureRequestModal: true });
  };

  columnDocumentationPopover = col => {
    return (
      <span id="popover-trigger-hover-focus">
        {col.loadingDocumentation ? <i className="fa fa-spin fa-spinner" /> : null}
        {col.documentation ? <div dangerouslySetInnerHTML={{ __html: col.documentation }} /> : null}
      </span>
    );
  };
  overviewDocumentationPopover = () => {
    const { taskTemplateDocumentation, loadingTaskTemplateDocumentation } = this.state;
    return (
      <span id="popover-trigger-hover-focus">
        {loadingTaskTemplateDocumentation ? <i className="fa fa-spin fa-spinner" /> : null}
        {taskTemplateDocumentation ? (
          <div dangerouslySetInnerHTML={{ __html: taskTemplateDocumentation }} />
        ) : null}
      </span>
    );
  };

  buildDocumentationItems = () => {
    const { intl } = this.props;
    const { columns, taskTemplateId, taskTemplateName, taskTemplateHasDocumentation } = this.state;

    const mainItem = {
      id: taskTemplateId,
      templateId: taskTemplateId,
      hasDocumentation: taskTemplateHasDocumentation,
      name: intl.formatMessage(listItemMsg('tasktemplate', taskTemplateId, taskTemplateName)),
    };

    const subItems = columns.map(col => ({
      id: col.id,
      templateId: col.id,
      hasDocumentation: col.hasDocumentation,
      name: formatListValue({ id: col.id, list: 'tasktemplate', fallback: col.name, intl }),
    }));

    return { mainItem, subItems };
  };

  openColumnDocumentationInNewTab = col => {
    const { mainItem, subItems } = this.buildDocumentationItems();
    openTaskDocumentationTab(mainItem, subItems, col.id);
  };

  openMainTaskDocumentationInNewTab = () => {
    const { mainItem, subItems } = this.buildDocumentationItems();
    openTaskDocumentationTab(mainItem, subItems, mainItem.id);
  };

  retrieveDocumentation = col => {
    if (col.documentation) return;
    const { columns } = this.state;
    this.setState(
      {
        columns: columns.map(x => {
          if (x.id === col.id) {
            return {
              ...x,
              loadingDocumentation: true,
            };
          }
          return x;
        }),
      },
      () => {
        api.base
          .get(`${config.appUrls.tasks}/api/tasktemplates/documentation/${col.id}`)
          .then(({ data }) => {
            this.setState({
              columns: columns.map(x => {
                if (x.id === col.id) {
                  return {
                    ...x,
                    documentation: sanitizer.sanitize(data),
                    loadingDocumentation: false,
                  };
                }
                return x;
              }),
            });
          });
      },
    );
  };

  retrieveMainTaskDocumentation = () => {
    const {
      taskTemplateHasDocumentation,
      taskTemplateDocumentation,
      currentSearchParams,
    } = this.state;

    if (taskTemplateDocumentation) return;
    this.setState(
      {
        loadingTaskTemplateDocumentation: true,
      },
      () => {
        api.base
          .get(
            `${config.appUrls.tasks}/api/tasktemplates/documentation/${currentSearchParams.taskFilters.taskId}`,
          )
          .then(({ data }) => {
            this.setState({
              loadingTaskTemplateDocumentation: false,
              taskTemplateDocumentation: sanitizer.sanitize(data),
            });
          });
      },
    );
  };

  buildColumnTitle(col) {
    return (
      <span>
        {formatListValue({
          id: col.id,
          list: 'tasktemplate',
          fallback: col.name,
          intl: this.props.intl,
        })}
        {col.hasDocumentation && (
          <span style={{ marginLeft: 7 }}>
            <PopoverStickOnHover
              trigger={['hover', 'focus']}
              placement="bottom"
              component={this.columnDocumentationPopover(col)}
              onMouseEnter={() => this.retrieveDocumentation(col)}
              onClick={() => this.openColumnDocumentationInNewTab(col)}
              shouldUpdatePosition
            >
              <span className="fa fa-book clickable" />
            </PopoverStickOnHover>
          </span>
        )}
      </span>
    );
  }

  formatDuration = (intl, x) => {
    const hours = intl.formatMessage(messages.HoursAbbreviated);
    const minutes = intl.formatMessage(messages.MinutesAbbreviated);

    return !x && x !== 0
      ? '-'
      : x > 60
      ? `${Math.floor(x / 60)}${hours} ${x % 60}${minutes}`
      : `${x}${minutes}`;
  };

  buildBadge = (id, isMain) => {
    if (!authService.hasFeature('CapacityPlanning')) return undefined;
    const { columnStatistics } = this.state;
    if (!columnStatistics) return undefined;
    const summary = columnStatistics[id];
    if (!summary) return undefined;

    const { intl } = this.props;
    let title = null;

    if (!isMain) {
      title =
        summary.totalTime > 0
          ? summary.totalTime !== summary.completedTime
            ? intl.formatMessage(messages.SubTaskExecutionDurationProgress, {
                total: this.formatDuration(intl, summary.totalTime),
                completed: this.formatDuration(intl, summary.completedTime),
              })
            : intl.formatMessage(messages.SubTaskExecutionDurationComplete, {
                total: this.formatDuration(intl, summary.totalTime),
              })
          : intl.formatMessage(messages.SubTaskNoExecutionDuration);
    }
    return <TaskProgressBadge percentage={summary.percentComplete} title={title} />;
  };

  buildTitle() {
    const { taskTemplateId, taskTemplateName, taskTemplateHasDocumentation } = this.state;
    if (!taskTemplateId) return this.props.intl.formatMessage(messages.TaskOverview);
    return (
      <span>
        {this.props.intl.formatMessage(messages.TaskOverview)}
        {!!taskTemplateId && ' - '}
        {!!taskTemplateId &&
          this.props.intl.formatMessage(
            listItemMsg('tasktemplate', taskTemplateId, taskTemplateName),
          )}
        {taskTemplateHasDocumentation ? (
          <span style={{ marginLeft: 7 }} className="clickable">
            <PopoverStickOnHover
              trigger={['hover', 'focus']}
              placement="bottom"
              component={this.overviewDocumentationPopover()}
              onMouseEnter={() => this.retrieveMainTaskDocumentation()}
              onClick={() => this.openMainTaskDocumentationInNewTab()}
              shouldUpdatePosition
            >
              <span className="fa fa-book clickable" />
            </PopoverStickOnHover>
          </span>
        ) : null}
      </span>
    );
  }

  render() {
    const canEdit = authService.hasPermission({
      functionality: 'PlannedTask',
      privilege: 'Update',
    });
    const { intl } = this.props;
    const {
      initialSearchParams,
      currentSearchParams,
      items,
      columns,
      templates,
      displayMode,
      allTasksSelected,
      showChangeTaskStatusModal,
      showChangeMainTaskFieldModal,
      showChangeSubtaskDeadlineModal,
      showChangeSubtaskExecutionDateModal,
      showResetDeadlineModal,
      creatingReport,
      showNewRegistrationModal,
      activeTask,
      showTaskDetailsModal,
      showRegistrationModal,
      showSendMailModal,
      showDeleteTaskModal,
      defaultResourceId,
      parameterDefinitions,
      allParameterDefinitions,
      languages,
      creatingAuthorizationReport,
      showBatchDeleteTaskModal,
      showShareItemModal,
      showGenerateDocumentsModal,
      showChangeTagsModal,
      showUpdateRelationTagsModal,
      showSelectDocumentsModal,
      signableDocuments,
      showSignatureRequestModal,
      activeDocuments,
      requiredAuthorizations,
    } = this.state;

    const { filter: columnFilters = {} } = currentSearchParams || initialSearchParams || {};
    const hasData = items && items.length > 0;

    const showAssignments = hasAssignments();

    return (
      <div>
        {showSignatureRequestModal && (
          <DocumentSignatureRequestModal
            show
            intl={intl}
            documents={activeDocuments}
            onSave={() =>
              this.setState({
                showSignatureRequestModal: false,
              })
            }
            onClose={() =>
              this.setState({ showSignatureRequestModal: false, activeDocument: undefined })
            }
          />
        )}
        {showSelectDocumentsModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectDocuments)}
            items={signableDocuments.map(x => ({ ...x, label: x.name }))}
            onSave={this.openSendSignatureRequestModal}
            onClose={() => this.setState({ showSelectDocumentsModal: false })}
            show={showSelectDocumentsModal}
            sortByLabel={false}
          />
        )}
        {showDeleteTaskModal && (
          <YesNoModal
            intl={intl}
            show={showDeleteTaskModal}
            title={intl.formatMessage(messages.DeleteTask)}
            message={intl.formatMessage(messages.DeleteMainTaskConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                showDeleteTaskModal: false,
                activeTask: undefined,
              }))
            }
            onSave={this.deleteTask}
          />
        )}
        {showBatchDeleteTaskModal && (
          <YesNoModal
            intl={intl}
            show={showBatchDeleteTaskModal}
            title={intl.formatMessage(messages.DeleteTask)}
            message={intl.formatMessage(messages.DeleteMainTaskConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                showBatchDeleteTaskModal: false,
              }))
            }
            onSave={this.executeBatchActionDeleteTask}
          />
        )}
        {showTaskDetailsModal && (
          <TaskDetailsModal
            show
            task={{ id: activeTask?.id }}
            onSave={this.onTaskDetailModalClosed}
            onClose={this.onTaskDetailModalClosed}
            disabled={!canEdit}
            onTaskDeleted={this.onTaskDeleted}
          />
        )}

        {showRegistrationModal && (
          <RegistrationModal
            {...getRegistrationModalPropsForTask(activeTask)}
            show={showRegistrationModal}
            onSave={this.onRegistrationCreated}
            onClose={() =>
              this.setState({
                activeTask: null,
                showRegistrationModal: false,
              })
            }
            disabled={!canEdit}
          />
        )}
        {showChangeTaskStatusModal && (
          <ChangeTaskStatusModal
            intl={intl}
            show
            subtasks={columns}
            relationLanguages={languages}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeTaskStatusModal: false,
              }))
            }
            onSave={this.executeBatchActionTaskStatus}
          />
        )}
        {showChangeMainTaskFieldModal && (
          <ChangeMainTaskFieldModal
            intl={intl}
            show
            parameterDefinitions={allParameterDefinitions}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeMainTaskFieldModal: false,
              }))
            }
            onSave={this.executeBatchActionChangeField}
          />
        )}
        {showChangeSubtaskDeadlineModal && (
          <ChangeSubtaskFieldModal
            field="deadline"
            intl={intl}
            show
            subtasks={columns}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeSubtaskDeadlineModal: false,
              }))
            }
            onSave={this.executeBatchActionSubtaskDeadline}
          />
        )}
        {showChangeSubtaskExecutionDateModal && (
          <ChangeSubtaskFieldModal
            field="executionDate"
            intl={intl}
            show
            subtasks={columns}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeSubtaskExecutionDateModal: false,
              }))
            }
            onSave={this.executeBatchActionSubtaskExecutionDate}
          />
        )}
        {showNewRegistrationModal && (
          <BatchRegistrationModal
            intl={intl}
            show
            tasks={columns}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showNewRegistrationModal: false,
              }))
            }
            referenceYear={initialSearchParams?.taskFilters?.year}
            defaultResourceId={defaultResourceId}
            onSave={this.executeBatchActionTaskRegistration}
            countSelectedRelations={this.getCountSelectedTasks()} // in this screen, the number of selected tasks equals the number of relations
          />
        )}
        {showSendMailModal && (
          <SendEmailModal
            intl={intl}
            show
            languages={languages}
            emailContext={11}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSendMailModal: false,
              }))
            }
            onSave={this.executeBatchActionSendTaskRelationMail}
          />
        )}
        {showGenerateDocumentsModal && (
          <BatchGenerateDocumentModal
            intl={intl}
            show
            languages={languages}
            subtasks={columns}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showGenerateDocumentsModal: false,
              }))
            }
            onSave={this.executeBatchActionGenerateDocuments}
          />
        )}
        {showResetDeadlineModal && (
          <ResetDeadlineModal
            intl={intl}
            show
            subtasks={columns}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showResetDeadlineModal: false,
              }))
            }
            onSave={this.executeBatchActionResetDeadline}
          />
        )}
        {showShareItemModal && (
          <ShareItemModal
            show
            type="task"
            id={activeTask.id}
            itemName={getShareItemNameForTask(activeTask, intl)}
            relationId={activeTask.relationId}
            relationName={getRelationName(intl, activeTask.relation)}
            onClose={() => this.setState({ showShareItemModal: false })}
          />
        )}
        {showChangeTagsModal && (
          <ChangeTaskTagsModal
            show
            onSave={this.executeBatchActionChangeTags}
            onClose={() => this.setState({ showChangeTagsModal: false })}
            addTags={showChangeTagsModal.addTags}
            subtasks={columns}
            intl={intl}
          />
        )}
        {showUpdateRelationTagsModal && (
          <UpdateRelationTagsModal
            intl={intl}
            show={showUpdateRelationTagsModal}
            onClose={() => this.setState({ showUpdateRelationTagsModal: false })}
            onSave={data => this.executeBatchActionUpdateRelationTags(data)}
          />
        )}
        <div className="fixed-filters">
          <SimplePanel article="5434846184722" title={this.buildTitle()}>
            {templates && (
              <TaskOverviewFilters
                filters={initialSearchParams.taskFilters}
                onFilterChange={filters => this.filtersChanged(filters)}
                displayMode={displayMode}
                displayModeChanged={this.displayModeChanged}
                countSelectedTasks={this.getCountSelectedTasksFormatted()}
                templates={templates}
                columnFilters={columnFilters}
              />
            )}
          </SimplePanel>

          <div className="row btn-row">
            <div className="col-md-12 pull-right content-right">
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.createReport()}
                disabled={!hasData || creatingReport}
              >
                <span className="margin5R">
                  <FaFileExcel className="file-icon" />
                </span>
                {intl.formatMessage(messages.ExportToExcel)}
              </Button>
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={this.createAuthorizationReport}
                disabled={!hasData || creatingAuthorizationReport}
              >
                <span className="margin5R">
                  {creatingAuthorizationReport && <i className="fa fa-spinner fa-spin" />}
                  {!creatingAuthorizationReport && <FaFileExcel className="file-icon" />}
                </span>
                {intl.formatMessage(messages.ExportAuthorizations)}
              </Button>
              <TaskGlobalActionsButton
                onChangeTaskStatus={this.openChangeSubtaskStatusModal}
                onChangeMainTaskField={this.openChangeMainTaskFieldModal}
                onChangeSubtaskDeadline={() =>
                  this.setState({
                    showChangeSubtaskDeadlineModal: true,
                  })
                }
                onChangeSubtaskExecutionDate={() =>
                  this.setState({
                    showChangeSubtaskExecutionDateModal: true,
                  })
                }
                onNewRegistration={() =>
                  this.setState({
                    showNewRegistrationModal: true,
                  })
                }
                onSendEmail={this.openSendEmailModal}
                onCreateReport={this.createReport}
                onDeleteTask={() =>
                  this.setState({
                    showBatchDeleteTaskModal: true,
                  })
                }
                onGenerateDocuments={this.openGenerateDocumentsModal}
                onResetDeadline={() =>
                  this.setState({
                    showResetDeadlineModal: true,
                  })
                }
                onAddTaskTags={() => {
                  this.setState({
                    showChangeTagsModal: { addTags: true },
                  });
                }}
                onRemoveTaskTags={() => {
                  this.setState({
                    showChangeTagsModal: { addTags: false },
                  });
                }}
                onUpdateRelationTags={() => {
                  this.setState({
                    showUpdateRelationTagsModal: true,
                  });
                }}
                disabled={this.isGlobalActionsDisabled() || !canEdit}
              />
            </div>
          </div>
        </div>
        {!initialSearchParams.taskFilters.taskId ? (
          <div className="panel">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-6 col-md-offset-3">
                  <p className="linebreak">
                    <FormattedMessage {...messages.TaskOverviewNoTaskSelectedDescription} />
                  </p>
                  <p className="linebreak">
                    <FormattedMessage {...messages.TaskOverviewNoTaskSelectedSelectTask} />
                    {templates && (
                      <select
                        onChange={this.templateChanged}
                        className="form-control margin10T"
                        value={initialSearchParams.taskFilters.taskId || ''}
                      >
                        {!initialSearchParams.taskFilters.taskId && (
                          <option>{intl.formatMessage(messages.SelectTask)}</option>
                        )}
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <ScrollableTable
            tableClassName="table-grid table-sticky"
            loadItems={this.loadItems}
            initialSearchParams={initialSearchParams}
            items={items}
            renderRow={this.renderRow}
            noDataLabel={<FormattedMessage {...messages.NoTasksFound} />}
          >
            <ColumnHeader
              className="sticky-column"
              title={
                initialSearchParams.taskFilters.taskId && (
                  <Checkbox
                    extraClass="margin2L"
                    onClick={this.handleSelectAllTasks}
                    value={allTasksSelected}
                  />
                )
              }
            />
            <ColumnHeader
              className="sticky-column"
              title={intl.formatMessage(messages.Relation)}
              sortKey="relation"
            />
            {requiredAuthorizations?.length > 0 ? (
              <ColumnHeader
                title={intl.formatMessage(messages.Authorization)}
                className="text-center"
                filter={{
                  type: 'items',
                  key: `withRequiredAuthorizationWarning`,
                  items: [false, true],
                  renderItem: item =>
                    item === false
                      ? intl.formatMessage(messages.WithoutMissingAuthorizations)
                      : intl.formatMessage(messages.WithMissingAuthorizations),
                }}
              />
            ) : null}
            {showAssignments ? (
              <ColumnHeader
                title={intl.formatMessage(messages.Assignment)}
                sortKey="assignmentName"
              />
            ) : null}
            <MultiFilterColumnHeader
              title={intl.formatMessage(messages.TaskStatus)}
              sortKey="status"
              className="text-center"
              filters={[
                {
                  type: 'items',
                  key: `mainStatus`,
                  items: [0, 1, 2, 3, 4, 5, 'divider', 7, 8],
                  renderItem: item => formatListValue({ id: item, list: 'extendedtaskstatus', intl }),
                },
                {
                  type: 'user',
                  key: `mainTaskUserFilter`,
                  withUnassigned: true,
                },
              ]}
              badge={this.buildBadge(this.state.taskTemplateId, true)}
            />

            {authService.hasFeature('referenceYear') ? (
              <ColumnHeader
                title={intl.formatMessage(messages.ReferenceYear)}
                sortKey="year"
                className="year-column"
              />
            ) : null}

            <ColumnHeader
              title={intl.formatMessage(messages.Deadline)}
              sortKey="deadline"
              className="deadline-column"
            />

            {displayMode === 'executionDate' && (
              <ColumnHeader
                title={intl.formatMessage(messages.ExecutionDate)}
                sortKey="mainExecutionDate"
                className="executiondate-column"
              />
            )}

            {parameterDefinitions &&
              parameterDefinitions.map(definition => (
                <ColumnHeader
                  key={definition.id}
                  title={definition.name}
                  className="text-center min100px"
                  sortKey={`parameter_${definition.id}`}
                  filter={definition.filter}
                />
              ))}

            {columns &&
              columns.map(col => (
                <MultiFilterColumnHeader
                  key={col.id}
                  title={this.buildColumnTitle(col)}
                  documentation={col.documentation}
                  sortKey={`subtask_${displayMode}_${col.id}`}
                  className="text-center"
                  filters={[
                    {
                      type: 'items',
                      key: `subtask_${col.id}`,
                      items: [0, 1, 2, 3, 4, 6, 5, 'divider', 7, 8],
                      renderItem: item =>
                        formatListValue({ id: item, list: 'extendedtaskstatus', intl }),
                    },
                    {
                      type: 'user',
                      key: `subtask_user_${col.id}`,
                      withUnassigned: true,
                    },
                    {
                      type: 'dateRange',
                      key: `subtask_completedDate_${col.id}`,
                    },
                  ]}
                  badge={this.buildBadge(col.id)}
                />
              ))}
            <ColumnHeader />
          </ScrollableTable>
        )}
      </div>
    );
  }
}

export default showWhenPermission(
  functionalities.PlannedTask,
  privileges.Read,
)(injectStateService(injectIntl(TaskOverviewContainer)));
